/**
 * GTM dataLayer events for storefront tracking.
 * Checkout events (add_shipping_info, add_payment_info, purchase) are handled
 * in the Shopify Custom Pixel — see custom-pixel-checkout-events.reference.js
 */
(function () {
  'use strict';

  if (window.__gtmEventsInitialized) return;
  window.__gtmEventsInitialized = true;

  window.dataLayer = window.dataLayer || [];

  const config = window.__gtmTracking || {};
  const SKIP_ADD_SOURCES = new Set(['cart-items', 'cart-drawer-discount']);

  let cartSnapshot = null;
  let viewCartTrackedThisOpen = false;

  function pushEvent(eventName, payload) {
    window.dataLayer.push({
      event: eventName,
      page_location: window.location.href,
      page_title: document.title,
      page_path: window.location.pathname,
      page_type: config.page?.type || '',
      currency: payload.currency || config.currency || '',
      ...payload,
    });
  }

  function centsToAmount(cents) {
    return Number(cents || 0) / 100;
  }

  function mapCartLineItem(item) {
    return {
      item_id: item.sku || String(item.variant_id || item.id || ''),
      item_name: item.product_title || item.title || '',
      item_variant: item.variant_title || '',
      item_brand: item.vendor || '',
      item_category: item.product_type || '',
      product_id: String(item.product_id || ''),
      variant_id: String(item.variant_id || item.id || ''),
      price: centsToAmount(item.final_price ?? item.price),
      quantity: Number(item.quantity || 1),
      line_price: centsToAmount(item.final_line_price ?? item.line_price),
      url: item.url || '',
      image_url: item.image || item.featured_image?.url || '',
    };
  }

  function mapProductItem(product) {
    if (!product) return null;
    const price = Number(product.price || 0);
    return {
      item_id: product.item_id || String(product.variant_id || ''),
      item_name: product.item_name || '',
      item_brand: product.item_brand || '',
      item_category: product.item_category || '',
      item_variant: product.item_variant || '',
      product_id: String(product.product_id || ''),
      variant_id: String(product.variant_id || ''),
      price,
      quantity: 1,
      url: product.url || '',
      image_url: product.image_url || '',
    };
  }

  function buildItemsPayload(items) {
    const normalized = items.filter(Boolean);
    const value = normalized.reduce((sum, item) => {
      const linePrice = item.line_price ?? Number(item.price || 0) * Number(item.quantity || 1);
      return sum + Number(linePrice || 0);
    }, 0);

    return {
      value: Number(value.toFixed(2)),
      items: normalized,
    };
  }

  async function fetchCart() {
    if (!window.routes?.cart_url) return null;
    try {
      const response = await fetch(`${window.routes.cart_url}.js`);
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      console.error('[gtm-events] fetchCart failed', error);
      return null;
    }
  }

  async function refreshCartSnapshot() {
    cartSnapshot = await fetchCart();
    return cartSnapshot;
  }

  function getCartDeltas(previousCart, nextCart) {
    const added = [];
    const removed = [];

    if (!nextCart?.items) return { added, removed };

    const previousMap = new Map(
      (previousCart?.items || []).map((item) => [String(item.variant_id || item.id), item])
    );
    const nextMap = new Map(
      nextCart.items.map((item) => [String(item.variant_id || item.id), item])
    );

    nextMap.forEach((nextItem, variantId) => {
      const previousItem = previousMap.get(variantId);
      const deltaQty = Number(nextItem.quantity) - Number(previousItem?.quantity || 0);
      if (deltaQty > 0) {
        const mapped = mapCartLineItem(nextItem);
        mapped.quantity = deltaQty;
        mapped.line_price = Number((mapped.price * deltaQty).toFixed(2));
        added.push(mapped);
      }
    });

    previousMap.forEach((previousItem, variantId) => {
      const nextItem = nextMap.get(variantId);
      const deltaQty = Number(nextItem?.quantity || 0) - Number(previousItem.quantity);
      if (deltaQty < 0) {
        const mapped = mapCartLineItem(previousItem);
        mapped.quantity = Math.abs(deltaQty);
        mapped.line_price = Number((mapped.price * mapped.quantity).toFixed(2));
        removed.push(mapped);
      }
    });

    return { added, removed };
  }

  function trackPageView() {
    pushEvent('page_view', {
      page_referrer: document.referrer || config.page?.referrer || '',
      customer_logged_in: Boolean(config.customerLoggedIn),
    });
  }

  function trackViewItem(product) {
    const item = mapProductItem(product);
    if (!item) return;

    const payload = buildItemsPayload([item]);
    pushEvent('view_item', {
      currency: config.currency,
      value: payload.value,
      items: payload.items,
      item_id: item.item_id,
      item_name: item.item_name,
      item_brand: item.item_brand,
      item_category: item.item_category,
      item_variant: item.item_variant,
      product_id: item.product_id,
      variant_id: item.variant_id,
      price: item.price,
    });
  }

  function trackAddToCart(items) {
    if (!items.length) return;
    const payload = buildItemsPayload(items);
    pushEvent('add_to_cart', {
      currency: config.currency,
      value: payload.value,
      items: payload.items,
    });
  }

  function trackRemoveFromCart(items) {
    if (!items.length) return;
    const payload = buildItemsPayload(items);
    pushEvent('remove_from_cart', {
      currency: config.currency,
      value: payload.value,
      items: payload.items,
    });
  }

  async function trackViewCart(cart) {
    const cartData = cart || (await fetchCart());
    if (!cartData?.items?.length) return;

    const items = cartData.items.map(mapCartLineItem);
    const payload = buildItemsPayload(items);

    pushEvent('view_cart', {
      currency: cartData.currency || config.currency,
      value: payload.value,
      items: payload.items,
      cart_id: cartData.token || String(cartData.item_count || ''),
      item_count: cartData.item_count || items.length,
    });
  }

  async function trackBeginCheckout() {
    const cartData = await fetchCart();
    if (!cartData?.items?.length) return;

    const items = cartData.items.map(mapCartLineItem);
    const payload = buildItemsPayload(items);

    pushEvent('begin_checkout', {
      currency: cartData.currency || config.currency,
      value: payload.value,
      items: payload.items,
      cart_id: cartData.token || '',
      item_count: cartData.item_count || items.length,
      coupon: getAppliedCoupon(cartData),
    });
  }

  function getAppliedCoupon(cartData) {
    const codes = cartData?.discount_codes || [];
    const applicable = codes.find((entry) => entry.applicable !== false);
    return applicable?.code || '';
  }

  function trackSignUp() {
    pushEvent('sign_up', {
      method: 'shopify_account',
      customer_id: config.customerId || '',
    });
  }

  function mapVariantToProduct(variant) {
    if (!variant) return null;
    const product = config.product || {};
    return {
      item_id: variant.sku || String(variant.id || product.variant_id || ''),
      item_name: product.item_name || '',
      item_brand: product.item_brand || '',
      item_category: product.item_category || '',
      item_variant: variant.title || product.item_variant || '',
      product_id: product.product_id || '',
      variant_id: String(variant.id || product.variant_id || ''),
      price: centsToAmount(variant.price),
      url: product.url || window.location.pathname,
      image_url: product.image_url || '',
    };
  }

  async function resolveCartState(cartData) {
    if (cartData?.items?.length) return cartData;
    // /cart/add.js returns a single line item at the root, not a full cart with `items`.
    return fetchCart();
  }

  function bindCartUpdateTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      const nextCart = await resolveCartState(event.cartData);
      if (!nextCart?.items?.length) return;

      const { added, removed } = getCartDeltas(cartSnapshot, nextCart);

      if (removed.length && event.source !== 'cart-drawer-discount') {
        trackRemoveFromCart(removed);
      }

      if (added.length && !SKIP_ADD_SOURCES.has(event.source)) {
        trackAddToCart(added);
      }

      cartSnapshot = nextCart;
    });
  }

  function bindVariantChangeTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (!config.product) return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event.data?.variant;
      if (!variant) return;
      trackViewItem(mapVariantToProduct(variant));
    });
  }

  function bindCheckoutTracking() {
    document.addEventListener(
      'click',
      (event) => {
        const checkoutButton = event.target.closest('#CartDrawer-Checkout, #CartPage-Checkout, .product-form__buy-now');
        if (!checkoutButton) return;
        if (checkoutButton.getAttribute('aria-disabled') === 'true' || checkoutButton.disabled) return;

        trackBeginCheckout();
      },
      true
    );
  }

  function bindRegisterTracking() {
    if (config.isRegisterPage) {
      const registerForm = document.querySelector('form[action*="account"]');
      registerForm?.addEventListener('submit', () => {
        try {
          sessionStorage.setItem('gtm_pending_sign_up', String(Date.now()));
        } catch (error) {
          /* ignore */
        }
      });
    }

    if (config.isAccountPage) {
      try {
        const pending = sessionStorage.getItem('gtm_pending_sign_up');
        const pendingAt = Number(pending);
        const isRecent = pending && !Number.isNaN(pendingAt) && Date.now() - pendingAt < 10 * 60 * 1000;
        if (isRecent) {
          sessionStorage.removeItem('gtm_pending_sign_up');
          trackSignUp();
        } else if (pending) {
          sessionStorage.removeItem('gtm_pending_sign_up');
        }
      } catch (error) {
        /* ignore */
      }
    }
  }

  function bindCartDrawerTracking() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

    const originalOpen = cartDrawer.open.bind(cartDrawer);
    cartDrawer.open = function (...args) {
      const result = originalOpen(...args);
      if (!viewCartTrackedThisOpen) {
        viewCartTrackedThisOpen = true;
        trackViewCart().finally(() => {
          setTimeout(() => {
            viewCartTrackedThisOpen = false;
          }, 500);
        });
      }
      return result;
    };
  }

  async function init() {
    trackPageView();

    if (config.product) {
      trackViewItem(config.product);
    }

    await refreshCartSnapshot();
    bindCartUpdateTracking();
    bindVariantChangeTracking();
    bindCheckoutTracking();
    bindRegisterTracking();
    bindCartDrawerTracking();

    if (config.cartPage) {
      await trackViewCart(cartSnapshot);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
